"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

/**
 * Hook to invalidate contact tags cache
 * Call after creating, updating, or deleting a contact
 */
export function useInvalidateContactTags() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    // Invalidate React Query cache
    await queryClient.invalidateQueries({
      queryKey: ["tags", "contacts"],
    });

    // Invalidate server-side Aurora cache
    try {
      await fetch("/api/tags/invalidate/contacts", {
        method: "POST",
      });
    } catch (error) {
      console.error("Failed to invalidate server-side contact tags cache:", error);
      // Don't throw - cache invalidation failure shouldn't break the app
    }
  }, [queryClient]);
}

/**
 * Hook to invalidate ticket tags cache
 * Call after creating, updating, or deleting a ticket
 */
export function useInvalidateTicketTags() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    // Invalidate React Query cache
    await queryClient.invalidateQueries({
      queryKey: ["tags", "tickets"],
    });

    // Invalidate server-side Aurora cache
    try {
      await fetch("/api/tags/invalidate/tickets", {
        method: "POST",
      });
    } catch (error) {
      console.error("Failed to invalidate server-side ticket tags cache:", error);
      // Don't throw - cache invalidation failure shouldn't break the app
    }
  }, [queryClient]);
}

/**
 * Hook to invalidate task tags cache
 * Call after creating, updating, or deleting a task
 */
export function useInvalidateTaskTags() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    // Invalidate React Query cache
    await queryClient.invalidateQueries({
      queryKey: ["tags", "tasks"],
    });

    // Invalidate server-side Aurora cache
    try {
      await fetch("/api/tags/invalidate/tasks", {
        method: "POST",
      });
    } catch (error) {
      console.error("Failed to invalidate server-side task tags cache:", error);
      // Don't throw - cache invalidation failure shouldn't break the app
    }
  }, [queryClient]);
}

/**
 * Hook to invalidate company tags cache
 * Call after creating, updating, or deleting a company
 */
export function useInvalidateCompanyTags() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    // Invalidate React Query cache
    await queryClient.invalidateQueries({
      queryKey: ["tags", "companies"],
    });

    // Invalidate server-side Aurora cache
    try {
      await fetch("/api/tags/invalidate/companies", {
        method: "POST",
      });
    } catch (error) {
      console.error("Failed to invalidate server-side company tags cache:", error);
      // Don't throw - cache invalidation failure shouldn't break the app
    }
  }, [queryClient]);
}

/**
 * Hook to invalidate deal tags cache
 * Call after creating, updating, or deleting a deal
 */
export function useInvalidateDealTags() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    // Invalidate React Query cache
    await queryClient.invalidateQueries({
      queryKey: ["tags", "deals"],
    });

    // Invalidate server-side Aurora cache
    try {
      await fetch("/api/tags/invalidate/deals", {
        method: "POST",
      });
    } catch (error) {
      console.error("Failed to invalidate server-side deal tags cache:", error);
      // Don't throw - cache invalidation failure shouldn't break the app
    }
  }, [queryClient]);
}

/**
 * Hook to invalidate company technologies cache
 * Call after creating, updating, or deleting a company
 */
export function useInvalidateCompanyTechnologies() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    // Invalidate React Query cache
    await queryClient.invalidateQueries({
      queryKey: ["tags", "company-technologies"],
    });

    // Invalidate server-side Aurora cache
    try {
      await fetch("/api/tags/invalidate/company-technologies", {
        method: "POST",
      });
    } catch (error) {
      console.error("Failed to invalidate server-side company technologies cache:", error);
      // Don't throw - cache invalidation failure shouldn't break the app
    }
  }, [queryClient]);
}

/**
 * Hook to invalidate all tag caches at once
 * Useful for bulk operations or when you're unsure which cache to invalidate
 */
export function useInvalidateAllTagCaches() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    // Invalidate all tag-related React Query caches
    await queryClient.invalidateQueries({
      queryKey: ["tags"],
    });

    // Invalidate all server-side Aurora caches
    try {
      await Promise.all([
        fetch("/api/tags/invalidate/contacts", { method: "POST" }),
        fetch("/api/tags/invalidate/tickets", { method: "POST" }),
        fetch("/api/tags/invalidate/tasks", { method: "POST" }),
        fetch("/api/tags/invalidate/companies", { method: "POST" }),
        fetch("/api/tags/invalidate/deals", { method: "POST" }),
        fetch("/api/tags/invalidate/company-technologies", { method: "POST" }),
      ]);
    } catch (error) {
      console.error("Failed to invalidate server-side tag caches:", error);
      // Don't throw - cache invalidation failure shouldn't break the app
    }
  }, [queryClient]);
}
